import { cloneDeep } from 'lodash-es';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { TeamItem } from '@/models/team';
import { createTeamPageHeaders } from '@/pages/system/team/TeamPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { teamService } from '@/services/teamService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IListParams, IPageOptions, ITeamPageFilters, ITeamUpdateResponse } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'id', order: 'desc' as const }];

function createTeamStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<TeamItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<ITeamPageFilters>({
        sportId: createRequiredSportFilter(),
        teamCustomTeamId: {
            value: null,
            title: 'team.filters.customTeamId',
            allowSaveState: true,
        },
        teamName: {
            value: null,
            title: 'team.filters.teamName',
            allowSaveState: true,
        },
        bookmakerId: {
            value: null,
            title: 'team.filters.bookmakerId',
            allowSaveState: true,
        },
        teamCustomTeamAssociationStatus: {
            value: null,
            title: 'team.filters.customTeamAssociationStatus',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.teamCustomTeamId.value = null;
        filters.teamName.value = null;
        filters.bookmakerId.value = null;
        filters.teamCustomTeamAssociationStatus.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createTeamPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));

    const hiddenColumns = ref<string[]>([]);

    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'team',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params: IListParams<Record<string, any>> = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Bookmaker', 'Country', 'AuditTable'],
            };

            const response = await teamService.list(params);
            items.value = (response.data.rows || []).map((row) => new TeamItem(row));
            total.value = response.data.total || 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    function mergeMutationResponse(response: { data: ITeamUpdateResponse }) {
        if (response.data.success === false) return;
        const row = response.data.row;
        if (!row) return;

        const idx = items.value.findIndex((i) => i.id === row.id);
        if (idx !== -1) {
            const next = [...items.value];
            next[idx] = new TeamItem(row);
            items.value = next;
            triggerRef(items);
        }
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        mergeMutationResponse,
        hasRefresh: false,
    };
}

export function useTeamStore(storeId: string) {
    const useStore = defineStore(storeId, () => createTeamStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'team', defaultSortBy),
    });
    return useStore();
}

export type TeamStoreSetup = ReturnType<typeof useTeamStore>;
